import React, { useEffect, useState } from 'react';
import { getSites } from '@/services/api/siteService';
import { Site, Device } from '@/types/api';
import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { Alert } from '@/components/ui/Alert';

const SitesPage: React.FC = () => {
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSiteId, setExpandedSiteId] = useState<string | null>(null);

  useEffect(() => {
    const fetchSites = async () => {
      try {
        setLoading(true);
        const response = await getSites();
        setSites(response.sites);
        setError(null);
      } catch (err) {
        setError('An error occurred while fetching sites');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchSites();
  }, []);

  const toggleSiteExpansion = (siteId: string) => {
    if (expandedSiteId === siteId) {
      setExpandedSiteId(null);
    } else {
      setExpandedSiteId(siteId);
    }
  };

  const renderDeviceList = (devices: Device[]) => {
    if (devices.length === 0) {
      return (
        <div className="py-1 px-4 text-gray-500 dark:text-gray-400 italic text-xs">
          No devices found for this site.
        </div>
      );
    }

    return (
      <div className="space-y-1 p-1">
        {devices.map((device) => (
          <div 
            key={device.objectId} 
            className="bg-gray-50 dark:bg-gray-900/40 py-1 px-3 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
          >
            <h4 className="font-medium text-sm text-gray-900 dark:text-gray-100">{device.tag}</h4>
            <div className="grid grid-cols-2 gap-1 text-xs text-gray-700 dark:text-gray-200">
              <div>
                <span className="font-medium text-gray-600 dark:text-gray-400">MAC:</span> {device.MAC}
              </div>
              <div>
                <span className="font-medium text-gray-600 dark:text-gray-400">SSID:</span> {device.SSID}
              </div>
              <div>
                <span className="font-medium text-gray-600 dark:text-gray-400">Device ID:</span> {device.deviceId}
              </div>
              <div>
                <span className="font-medium text-gray-600 dark:text-gray-400">Mode:</span> {device.mode}
              </div>
              <div>
                <span className="font-medium text-gray-600 dark:text-gray-400">Access Level:</span> {device.accessLevel}
              </div>
              <div>
                <span className="font-medium text-gray-600 dark:text-gray-400">Address:</span> {device.address}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <Alert type="danger">{error}</Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-2">
      <h1 className="text-2xl font-bold mb-2 text-gray-900 dark:text-gray-100">Sites</h1>
      
      {sites.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 text-center">
          <p className="text-gray-500 dark:text-gray-400">No sites found.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sites.map((site) => (
            <Card key={site.objectId} className="overflow-hidden">
              <div 
                className="py-1 px-4 cursor-pointer flex justify-between items-center"
                onClick={() => toggleSiteExpansion(site.objectId)}
              >
                <div className="flex items-center space-x-4 flex-1">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base font-semibold truncate text-gray-900 dark:text-gray-100">{site.name}</h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                      {[site.street, site.city, site.state, site.postcode, site.country]
                        .filter(Boolean)
                        .join(', ')}
                    </p>
                    <div className="flex items-center mt-1 space-x-2">
                      {site.email && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{site.email}</p>}
                      <span className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 px-1.5 py-0.5 rounded">
                        {site.device_list.length} {site.device_list.length === 1 ? 'Device' : 'Devices'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-gray-400 dark:text-gray-400 ml-2 flex-shrink-0">
                  <svg 
                    className={`w-5 h-5 transform transition-transform ${expandedSiteId === site.objectId ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              
              {expandedSiteId === site.objectId && (
                <div className="border-t border-gray-200 dark:border-gray-700">
                  <div className="bg-gray-50 dark:bg-gray-900/40 py-1 px-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="font-medium text-sm text-gray-900 dark:text-gray-100">Devices</h3>
                  </div>
                  {renderDeviceList(site.device_list)}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default SitesPage; 
